import { useState, useEffect, useCallback } from 'react'; // eslint-disable-line
import { RefreshCw, Download, Database, CheckCircle, AlertTriangle, Clock, Award, TrendingUp } from 'lucide-react';
import HarnessWorkflowGuide from './HarnessWorkflowGuide';

const API_BASE = ''; // 상대 경로 → setupProxy가 /api를 백엔드로 전달

const QUALITY_COLORS = {
  gold: { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800' },
  silver: { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-700', badge: 'bg-gray-200 text-gray-800' },
  bronze: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' },
  needs_human: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', badge: 'bg-red-100 text-red-800' },
};

const QUALITY_LABELS = {
  gold: 'Gold',
  silver: 'Silver',
  bronze: 'Bronze',
  needs_human: '검토 필요',
};

const HarnessDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/harness/stats`);
      const json = await res.json();
      if (json.success) {
        setStats(json.data);
      } else {
        setError(json.error || '통계 조회 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleExportJSONL = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/api/harness/export-jsonl?quality=gold,silver&limit=10000`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `docuai_distill_${Date.now()}.jsonl`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('내보내기 실패: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const summary = stats?.summary || {};
  const total = parseInt(summary.total) || 0;
  const gold = parseInt(summary.gold) || 0;
  const silver = parseInt(summary.silver) || 0;
  const bronze = parseInt(summary.bronze) || 0;
  const needsHuman = parseInt(summary.needs_human) || 0;
  const usedInTraining = parseInt(summary.used_in_training) || 0;
  const avgConfidence = parseFloat(summary.avg_confidence) || 0;
  const trainReady = gold + silver;
  const progress = Math.min(100, Math.round((trainReady / 10000) * 100));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Database size={20} className="text-blue-600" />
            증류 파이프라인 대시보드
            <HarnessWorkflowGuide className="ml-2" />
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Gemini(Teacher) → Qwen3-VL(Student) 증류용 학습 데이터 현황
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchStats}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            새로고침
          </button>
          <button
            onClick={handleExportJSONL}
            disabled={exporting || trainReady === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Download size={14} />
            {exporting ? '내보내는 중...' : `JSONL 내보내기 (${trainReady}건)`}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Progress Bar - 10,000건 목표 */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
            <TrendingUp size={16} className="text-blue-600" />
            증류 데이터 축적 현황
          </span>
          <span className="text-sm text-gray-500">
            <span className="font-bold text-blue-700">{trainReady.toLocaleString()}</span> / 10,000건 목표
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-blue-500 to-blue-600"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>0</span>
          <span>{progress}%</span>
          <span>10,000</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="전체 데이터"
          value={total}
          icon={Database}
          color="blue"
        />
        <SummaryCard
          label="학습 가능 (Gold+Silver)"
          value={trainReady}
          icon={Award}
          color="green"
        />
        <SummaryCard
          label="평균 신뢰도"
          value={`${(avgConfidence * 100).toFixed(1)}%`}
          icon={CheckCircle}
          color="purple"
        />
        <SummaryCard
          label="학습 사용됨"
          value={usedInTraining}
          icon={TrendingUp}
          color="indigo"
        />
      </div>

      {/* Quality Distribution */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">품질 등급 분포</h3>
        <div className="grid grid-cols-4 gap-3">
          {[
            { key: 'gold', count: gold, icon: '🥇' },
            { key: 'silver', count: silver, icon: '🥈' },
            { key: 'bronze', count: bronze, icon: '🥉' },
            { key: 'needs_human', count: needsHuman, icon: '👤' },
          ].map(({ key, count, icon }) => {
            const colors = QUALITY_COLORS[key];
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <div key={key} className={`${colors.bg} ${colors.border} border rounded-lg p-3 text-center`}>
                <div className="text-2xl mb-1">{icon}</div>
                <div className={`text-xl font-bold ${colors.text}`}>{count.toLocaleString()}</div>
                <div className="text-xs text-gray-500">{QUALITY_LABELS[key]} ({pct}%)</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail Table by Document Type */}
      {stats?.byCategory?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">문서 타입별 상세</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">문서 타입</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">품질</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-600">검증 방식</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-600">건수</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-600">평균 신뢰도</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-600">평균 일치율</th>
                </tr>
              </thead>
              <tbody>
                {stats.byCategory.map((row, i) => {
                  const qColors = QUALITY_COLORS[row.quality_label] || QUALITY_COLORS.needs_human;
                  return (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 font-medium">{row.document_type}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${qColors.badge}`}>
                          {QUALITY_LABELS[row.quality_label] || row.quality_label}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center text-gray-500">
                        {row.verification_method === 'cross_match' && <span className="flex items-center justify-center gap-1"><CheckCircle size={12} className="text-green-500" /> 교차검증</span>}
                        {row.verification_method === 'rule_based' && <span className="flex items-center justify-center gap-1"><Clock size={12} className="text-blue-500" /> 규칙</span>}
                        {row.verification_method === 'ai_judge' && <span className="flex items-center justify-center gap-1"><AlertTriangle size={12} className="text-yellow-500" /> AI 심판</span>}
                        {row.verification_method === 'human' && <span className="flex items-center justify-center gap-1"><CheckCircle size={12} className="text-purple-500" /> 사용자</span>}
                      </td>
                      <td className="py-2 px-3 text-right font-mono">{parseInt(row.count).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono">{(parseFloat(row.avg_confidence) * 100).toFixed(1)}%</td>
                      <td className="py-2 px-3 text-right font-mono">{parseFloat(row.avg_match_rate).toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, icon: Icon, color }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    indigo: 'bg-indigo-50 text-indigo-600',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg ${colorMap[color]}`}>
          <Icon size={16} />
        </div>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
};

export default HarnessDashboard;
